var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/*
 * <vitesse-resizable-column> — ONE column of a right-anchored stack.
 *
 * A stack of these is how a surface shows several places at once: the one being worked in, and the
 * ones it was reached through beside it. Each column owns its own width, drags by its own left
 * edge, and remembers what it was dragged to — which is what makes a stack feel like several rails
 * rather than one rail with dividers drawn on it.
 *
 * THE GEOMETRY, because it is the part that is easy to get backwards. The stack is anchored to the
 * RIGHT edge of the screen, so every column's left edge is the boundary that moves: dragging a
 * column's wall to the left widens THAT column and pushes everything to its left further left.
 * Nothing to its right moves, and no column ever steals width from its neighbour. That is the same
 * rule the rail has always had for its own wall — this is that control, per column.
 *
 * Generic on purpose: it knows nothing about conversations, agents or viewers. A fleet viewport or
 * the console can compose the same columns, and the drag will feel identical because it IS the same
 * controller and the same wall (`resizeWallCss`).
 *
 * WIDTH IS ANNOUNCED, NOT READ BACK. A host that composes several of these needs their total (to
 * size the rail around them), and reading children's widths after a render is how a layout goes one
 * frame stale. So every change — a drag, a restore, a key change — fires `column-resize`.
 */
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from '../i18n';
import { LocaleController } from '../i18n/LocaleController';
import { RailResizeController, resizeWallCss, RAIL_MIN_W, RAIL_MAX_W, RAIL_DEFAULT_W } from './rail-resize';
import './vitesse-icon';
let VitesseResizableColumn = class VitesseResizableColumn extends LitElement {
    constructor() {
        super(...arguments);
        this.minWidth = RAIL_MIN_W;
        this.maxWidth = RAIL_MAX_W;
        this.defaultWidth = RAIL_DEFAULT_W;
        /** Drop the grip. For a column whose left edge something else already draws — the outermost
         *  column of a rail that draws its own wall — so two handles never stack on one line. */
        this.noGrip = false;
        /** Where the neighbour was when this drag began, so every move is measured from one origin rather
         *  than accumulating rounding as the pointer travels. */
        this.neighbourStart = 0;
        this.resize = new RailResizeController(this, {
            minWidth: () => this.minWidth,
            maxWidth: () => Math.min(this.maxWidth, this.ceiling?.() ?? Infinity),
            defaultWidth: () => this.defaultWidth,
            storageKey: () => this.storageKey,
            onChange: (width) => this.announce(width),
            onDragStart: () => {
                this.neighbourStart = this.sharesWallWith?.()?.width ?? 0;
            },
            onDragEnd: () => this.sharesWallWith?.()?.persist(),
            share: (delta) => {
                const neighbour = this.sharesWallWith?.();
                if (!neighbour)
                    return delta;
                // What it can give, which at its own floor is less than what was asked.
                neighbour.setWidth(this.neighbourStart - delta, false);
                return this.neighbourStart - neighbour.width;
            },
        });
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- re-renders on locale change
        this.locale = new LocaleController(this);
    }
    /** The width this column is at, for a host that needs the total. */
    get width() {
        return this.resize.width;
    }
    /** Is a wall being dragged right now? A host correcting the layout must keep its hands off while
     *  it is: the correction reads the width this column was last dragged TO, which during a drag is
     *  the width before the drag started — so it would undo every frame of the gesture. */
    get resizing() {
        return this.resize.resizing;
    }
    /** What it was last dragged to, which is what it wants back when a column closes beside it. */
    get desiredWidth() {
        return this.resize.desired;
    }
    /** Set it from outside — the other half of a drag on a shared wall. */
    setWidth(px, persist = true) {
        this.resize.setWidth(px, persist);
    }
    /** Write the current width down. Called on the neighbour when a shared drag ends, so both sides of
     *  the boundary are remembered rather than only the one the pointer was on. */
    persist() {
        this.resize.setWidth(this.resize.width);
    }
    willUpdate(changed) {
        // A column does not move between positions; the CONTENT moves through it. So when the key
        // changes this element is now a different column of the stack and must wear that column's
        // remembered width rather than carrying the previous one's across.
        if (changed.has('storageKey') && changed.get('storageKey') !== undefined)
            this.resize.restore();
    }
    announce(width) {
        this.style.setProperty('--col-w', `${width}px`);
        this.style.setProperty('--col-min', `${this.minWidth}px`);
        this.toggleAttribute('resizing', this.resize.resizing);
        this.dispatchEvent(new CustomEvent('column-resize', { detail: { width }, bubbles: true, composed: true }));
    }
    static { this.styles = [
        resizeWallCss,
        css `
      :host {
        /*
         * THE DRAGGED WIDTH IS A BASIS, NOT A FLOOR.
         *
         * The stack is capped — by the rail's own maximum and by the room the app keeps — and the
         * columns inside it are not, so three columns dragged wide would sum past the cap. Overflow
         * on the rail does not scroll, it CLIPS, and the half cut off would be the leftmost column:
         * the one being worked in.
         *
         * So they shrink, together, the way a flex row is meant to. Nobody is ever cut off, and the
         * moment there is room again every column is back at exactly what it was dragged to —
         * because the basis is what was dragged, and shrinking never wrote to it.
         */
        flex: 0 1 var(--col-w, 360px);
        /*
         * …BUT NEVER BELOW ITS OWN FLOOR. Flex shrinks proportionally to the basis, which does not
         * know about minimums — three columns sharing a 900px cap left the outermost at 200px, too
         * narrow to read a thread in, which is the state the fit limit exists to prevent. With the
         * floor in place they come to rest at their minimum instead, and the fit limit guarantees
         * that many minimums fit: a column is either usable or it is not drawn.
         */
        min-width: var(--col-min, 300px);
        position: relative;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }
      /* A width transition driven by a pointermove handler makes the edge lag the cursor, which
         reads as a broken drag rather than a smoothed one. */
      :host([resizing]) {
        transition: none;
      }
      .inner {
        flex: 1;
        min-height: 0;
        min-width: 0;
        display: flex;
        flex-direction: column;
        /* Its own stacking context, so content's sticky headers cannot rise above the wall. */
        position: relative;
        z-index: 0;
      }
    `,
    ]; }
    render() {
        return html `
      ${this.noGrip ? null : html `<div class="wall" @pointerdown=${this.resize.start} title=${t('ai.resize')}></div>`}
      <div class="inner"><slot></slot></div>
    `;
    }
    updated() {
        this.style.setProperty('--col-w', `${this.resize.width}px`);
        this.style.setProperty('--col-min', `${this.minWidth}px`);
        this.toggleAttribute('resizing', this.resize.resizing);
    }
};
__decorate([
    property({ attribute: 'storage-key' })
], VitesseResizableColumn.prototype, "storageKey", void 0);
__decorate([
    property({ type: Number, attribute: 'min-width' })
], VitesseResizableColumn.prototype, "minWidth", void 0);
__decorate([
    property({ type: Number, attribute: 'max-width' })
], VitesseResizableColumn.prototype, "maxWidth", void 0);
__decorate([
    property({ type: Number, attribute: 'default-width' })
], VitesseResizableColumn.prototype, "defaultWidth", void 0);
__decorate([
    property({ type: Boolean, attribute: 'no-grip', reflect: true })
], VitesseResizableColumn.prototype, "noGrip", void 0);
__decorate([
    property({ attribute: false })
], VitesseResizableColumn.prototype, "sharesWallWith", void 0);
__decorate([
    property({ attribute: false })
], VitesseResizableColumn.prototype, "ceiling", void 0);
VitesseResizableColumn = __decorate([
    customElement('vitesse-resizable-column')
], VitesseResizableColumn);
export { VitesseResizableColumn };
//# sourceMappingURL=vitesse-resizable-column.js.map